import { requestHandlerFabric } from 'api/instance';
import { getApiPrefix, getKitApiPrefix } from 'common/constants';
import type {
  IKitWorkspace,
  ICompanionsSchema,
  ICompanionsQuery,
  ISaveAssemblyPayload,
  IAddAccessoryPayload,
} from './types';

// Kit Builder answers on the same host as the configurator API but under its own
// prefix, so everything here builds paths from apiBase() — EXCEPT cloneSession:
// client-sessions is a configurator resource, not a kit one, and kept /api/v1.
const apiBase = () => getKitApiPrefix();
const prefix = () => `${apiBase()}/kit-workspaces`;

// ============ Workspace ============

export const createWorkspace = async (sessionId: string): Promise<{ workspaceKey: string }> => (
  await requestHandlerFabric().post<{ workspaceKey: string }>(prefix(), { sessionId })
).data;

export const getWorkspace = async (key: string): Promise<IKitWorkspace> => (
  await requestHandlerFabric().get<IKitWorkspace>(`${prefix()}/${key}`)
).data;

export const completeWorkspace = async (
  key: string,
  accentColor?: string,
  logoUrl?: string,
): Promise<{ status: string }> => (
  await requestHandlerFabric().post<{ status: string }>(
    `${prefix()}/${key}/complete`,
    {
      ...(accentColor ? { accentColor } : {}),
      ...(logoUrl ? { logoUrl } : {}),
    },
  )
).data;

export const getPdfUrl = async (key: string): Promise<{ url: string }> => (
  await requestHandlerFabric().get<{ url: string }>(`${prefix()}/${key}/pdf-url`)
).data;

// Start Over that keeps the kit structure: drops all assemblies + kit lines +
// accessories and returns the workspace to draft, leaving the empty kit shells.
export const resetWorkspace = async (key: string): Promise<{ status: string }> => (
  await requestHandlerFabric().post<{ status: string }>(`${prefix()}/${key}/reset`, {})
).data;

// Combined-order HTML for the "Combined PDF preview" popup (same template the
// merged PDF uses). Read-only, but POST: the logo can be a legacy base64
// data: URI, which is far too large for a query string.
export const getWorkspacePreview = async (
  key: string,
  accentColor?: string,
  logoUrl?: string,
): Promise<{ html: string }> => (
  await requestHandlerFabric().post<{ html: string }>(`${prefix()}/${key}/preview`, {
    ...(accentColor ? { accent: accentColor } : {}),
    ...(logoUrl ? { logo: logoUrl } : {}),
  })
).data;

// ============ Assemblies (library items, no qty) ============

export const saveAssembly = async (
  key: string,
  payload: ISaveAssemblyPayload,
): Promise<{ assemblyId: string }> => (
  await requestHandlerFabric().post<{ assemblyId: string }>(`${prefix()}/${key}/assemblies`, payload)
).data;

// Accepts a meta-only patch ({name, notes}) or the full save payload when the
// configuration itself changed after the assembly was committed.
export const patchAssembly = async (
  assemblyId: string,
  payload: Partial<ISaveAssemblyPayload>,
): Promise<{ ok: boolean }> => (
  await requestHandlerFabric().patch<{ ok: boolean }>(`${apiBase()}/assemblies/${assemblyId}`, payload)
).data;

export const copyAssembly = async (assemblyId: string): Promise<{ assemblyId: string }> => (
  await requestHandlerFabric().post<{ assemblyId: string }>(`${apiBase()}/assemblies/${assemblyId}/copy`)
).data;

export const deleteAssembly = async (assemblyId: string): Promise<void> => {
  await requestHandlerFabric().delete(`${apiBase()}/assemblies/${assemblyId}`);
};

// ============ Kit lines (assembly <-> kit membership + qty) ============

export const addKitLine = async (
  kitUuid: string,
  payload: { assemblyUuid: string; qty: number; position?: number },
): Promise<{ kitLineId: string }> => (
  await requestHandlerFabric().post<{ kitLineId: string }>(`${apiBase()}/kits/${kitUuid}/lines`, payload)
).data;

export const patchKitLine = async (
  lineId: string,
  payload: { qty: number },
): Promise<{ ok: boolean }> => (
  await requestHandlerFabric().patch<{ ok: boolean }>(`${apiBase()}/kit-lines/${lineId}`, payload)
).data;

export const deleteKitLine = async (lineId: string): Promise<void> => {
  await requestHandlerFabric().delete(`${apiBase()}/kit-lines/${lineId}`);
};

// ============ Kits ============

export const createKit = async (
  key: string,
  payload: { name?: string; notes?: string },
): Promise<{ kitId: string }> => (
  await requestHandlerFabric().post<{ kitId: string }>(`${prefix()}/${key}/kits`, payload)
).data;

export const patchKit = async (
  kitId: string,
  payload: { name?: string; notes?: string; collapsed?: boolean },
): Promise<{ ok: boolean }> => (
  await requestHandlerFabric().patch<{ ok: boolean }>(`${apiBase()}/kits/${kitId}`, payload)
).data;

export const deleteKit = async (kitId: string): Promise<void> => {
  await requestHandlerFabric().delete(`${apiBase()}/kits/${kitId}`);
};

// ============ Accessories (companions) ============

// POST (not GET): the category filter is sent as an array, which does not
// survive a query string cleanly. search/limit/offset moved into the body too.
export const companionsSchema = async (
  key: string,
  query: ICompanionsQuery,
): Promise<ICompanionsSchema> => (
  await requestHandlerFabric().post<ICompanionsSchema>(`${prefix()}/${key}/companions/schema`, query)
).data;

export const addKitAccessory = async (
  kitUuid: string,
  payload: IAddAccessoryPayload,
): Promise<{ accessoryId: string }> => (
  await requestHandlerFabric().post<{ accessoryId: string }>(`${apiBase()}/kits/${kitUuid}/accessories`, payload)
).data;

export const addAssemblyAccessory = async (
  assemblyUuid: string,
  payload: IAddAccessoryPayload,
): Promise<{ accessoryId: string }> => (
  await requestHandlerFabric().post<{ accessoryId: string }>(`${apiBase()}/assemblies/${assemblyUuid}/accessories`, payload)
).data;

/**
 * Add a companion accessory to either a kit or a standalone assembly.
 * Exactly one of kitUuid / assemblyUuid must be provided (XOR), matching the
 * backend ownership constraint.
 */
export const addAccessory = async (
  target: { kitUuid?: string; assemblyUuid?: string },
  payload: IAddAccessoryPayload,
): Promise<{ accessoryId: string }> => {
  if (target.kitUuid) return addKitAccessory(target.kitUuid, payload);
  if (target.assemblyUuid) return addAssemblyAccessory(target.assemblyUuid, payload);
  throw new Error('addAccessory requires kitUuid or assemblyUuid');
};

export const patchAccessory = async (
  accessoryId: string,
  payload: { qty?: number; notes?: string },
): Promise<{ ok: boolean }> => (
  await requestHandlerFabric().patch<{ ok: boolean }>(`${apiBase()}/accessories/${accessoryId}`, payload)
).data;

export const deleteAccessory = async (accessoryId: string): Promise<void> => {
  await requestHandlerFabric().delete(`${apiBase()}/accessories/${accessoryId}`);
};

// ============ Session clone ("Save and Copy") ============

// Stays on the configurator API: client-sessions is a core CPQ resource, not a
// Kit Builder one, so it kept the /api/v1 prefix and the main host.
export const cloneSession = async (sessionUuid: string): Promise<{ sessionId: string }> => (
  await requestHandlerFabric().post<{ sessionId: string }>(`${getApiPrefix()}/client-sessions/${sessionUuid}/clone`)
).data;
